import React, { useMemo, useState } from 'react';

import { PlusOutlined, QuestionCircleFilled } from '@ant-design/icons';
import { Tag, Button, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import Can from '@shared/permissions/Can';
import FullDayRangePicker from '@components/shared/FullDayRangePicker';
import InventoryTransformationConceptModal from '@shared/inventory_transformations/InventoryTransformationConceptModal';
import ListPageHeader from '@shared/ListPageHeader';
import TableIndex from '@shared/table/TableIndex';
import type { SupportRule } from '@shared/table/TableIndex';
import { MultipleValue } from '@shared/table/Rules/component';

import {
  inventoryTransformationService,
  type InventoryTransformationStatus,
} from '@services/admin/inventoryTransformationService';

// TableService.getRecords 預期 `Promise<{ data, has_more }>`，但
// `inventoryTransformationService.getRecords` 因為走 `parseAdminApiResponse`
// 會回傳 `success-envelope | ApiErrorEnvelope` 的 union。Context.jsx 內部直接
// 解 `data.data / data.has_more`，碰到 error envelope 會 silent 拿到 undefined。
// 這裡 adapter 在邊界把 error envelope throw 起來，讓 react-query 走錯誤路徑。
const tableService = {
  ...inventoryTransformationService,
  getRecords: async (params: Record<string, unknown>) => {
    const res = await inventoryTransformationService.getRecords(params);
    if (!res.success) throw new Error(`InventoryTransformations getRecords failed: ${res.message}`);
    return { data: res.data, has_more: res.has_more };
  },
};

interface Row {
  id: string;
  name?: string;
  kind?: 'attribute_change' | 'purpose_reallocation' | string;
  status?: InventoryTransformationStatus | string;
  applicant_name?: string;
  approver_name?: string;
  warehouse_name?: string;
  created_at?: string;
  [key: string]: unknown;
}

const STATUS_COLOR: Record<InventoryTransformationStatus, string> = {
  pending: 'default',
  processing: 'processing',
  applied: 'success',
  partially_applied: 'warning',
  failed: 'error',
  rejected: 'default',
};

const STATUS_LABEL_KEY: Record<InventoryTransformationStatus, string> = {
  pending: 'enums.inventory_transformation.status.pending',
  processing: 'enums.inventory_transformation.status.processing',
  applied: 'enums.inventory_transformation.status.applied',
  partially_applied: 'enums.inventory_transformation.status.partially_applied',
  failed: 'enums.inventory_transformation.status.failed',
  rejected: 'enums.inventory_transformation.status.rejected',
};

const isKnownStatus = (s?: string): s is InventoryTransformationStatus =>
  !!s && s in STATUS_COLOR;

export default function AdminInventoryTransformationsList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [lastModified, setLastModified] = useState(0);
  const [conceptModalVisible, setConceptModalVisible] = useState(false);

  const columns: unknown[] = [
    {
      name: t('models.inventory_transformation.name'),
      selector: (row: Row) => row.name,
      sortable: true,
      sortFieldName: 'inventory_transformations.name',
      cell: (row: Row) => <Link to={`/inventory_transformations/${row.id}`}>{row.name}</Link>,
    },
    {
      name: t('models.inventory_transformation.kind'),
      selector: (row: Row) => row.kind,
      cell: (row: Row) => (
        <Tag color={row.kind === 'purpose_reallocation' ? 'blue' : 'default'}>
          {t(`enums.inventory_transformation.kind.${row.kind}`, row.kind ?? '')}
        </Tag>
      ),
    },
    {
      name: t('models.inventory_transformation.status'),
      selector: (row: Row) => row.status,
      cell: (row: Row) => {
        const status = row.status;
        if (isKnownStatus(status)) {
          return (
            <Tag color={STATUS_COLOR[status]}>{t(STATUS_LABEL_KEY[status])}</Tag>
          );
        }
        return <Tag color="default">{status ?? ''}</Tag>;
      },
    },
    {
      name: t('models.inventory_transformation.warehouse'),
      selector: (row: Row) => row.warehouse_name,
    },
    {
      name: t('models.inventory_transformation.applicant'),
      selector: (row: Row) => row.applicant_name,
    },
    {
      name: t('models.inventory_transformation.created_at'),
      selector: (row: Row) => row.created_at,
      sortable: true,
      sortFieldName: 'inventory_transformations.created_at',
    },
  ];

  const supportRules = useMemo<SupportRule[]>(() => [
    {
      label: t('models.inventory_transformation.name'),
      component: MultipleValue,
      inputName: 'inventory_transformations.name',
      defaultPinned: true,
      createable: true,
    },
    {
      label: t('models.inventory_transformation.status'),
      component: MultipleValue,
      inputName: 'inventory_transformations.status',
      createable: false,
      defaultOptions: [
        { label: t('enums.inventory_transformation.status.pending'),           value: 'pending'           },
        { label: t('enums.inventory_transformation.status.processing'),        value: 'processing'        },
        { label: t('enums.inventory_transformation.status.applied'),           value: 'applied'           },
        { label: t('enums.inventory_transformation.status.partially_applied'), value: 'partially_applied' },
        { label: t('enums.inventory_transformation.status.failed'),            value: 'failed'            },
        { label: t('enums.inventory_transformation.status.rejected'),          value: 'rejected'          },
      ],
    },
    {
      label: t('models.inventory_transformation.kind'),
      component: MultipleValue,
      inputName: 'inventory_transformations.kind',
      createable: false,
      defaultOptions: [
        { label: t('enums.inventory_transformation.kind.attribute_change'),     value: 'attribute_change'     },
        { label: t('enums.inventory_transformation.kind.purpose_reallocation'), value: 'purpose_reallocation' },
        { label: t('enums.inventory_transformation.kind.auto_reclassify'),      value: 'auto_reclassify'      },
      ],
    },
    {
      label: t('models.inventory_transformation.created_at'),
      component: FullDayRangePicker,
      inputName: 'inventory_transformations.created_at',
    },
  ], [t]);

  return (
    <div>
      <ListPageHeader
        basePath="/admin"
        items={['stocks', 'stocks.inventory_transformations']}
        guideButton={
          <Tooltip title={t('concepts.inventory_transformations.tooltip')}>
            <Button
              type="text"
              size="small"
              icon={<QuestionCircleFilled style={{ color: '#1890ff' }} />}
              onClick={() => setConceptModalVisible(true)}
              style={{ padding: 0, height: 'auto', minWidth: 'auto' }}
            />
          </Tooltip>
        }
        actions={
          <Can requiredPermission="inventory_transformations:write">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/inventory_transformations/new')}
            >
              {t('common.button.new')}
            </Button>
          </Can>
        }
      />
      <TableIndex
        columns={columns}
        tableId="AdminInventoryTransformationsTable"
        service={tableService}
        defaultQuery={{
          sorting_field: 'inventory_transformations.created_at',
          sorting_direction: 'desc',
        }}
        supportRules={supportRules}
        searchable={{
          match: 'contains',
          searchFields: [
            t('models.inventory_transformation.name'),
            t('models.inventory_transformation.note'),
          ],
        }}
        lastModified={lastModified}
        setLastModified={setLastModified}
      />
      <InventoryTransformationConceptModal
        visible={conceptModalVisible}
        onClose={() => setConceptModalVisible(false)}
      />
    </div>
  );
}
